import React, { useEffect, useState, useRef } from 'react';
import {
    Button,
    FormControl,
    FormControlLabel,
    Grid,
    makeStyles,
    Radio,
    RadioGroup,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
    RegistrationStatuses,
    useGetContractRegistrationProcessQuery,
    useGetContractToRegistrationQuery,
    useRegisterContractMutation,
} from '../../../services/rtkApi';
import type { IContractToRegistrationProcessActivity } from '../../../classes/ContractToRegistrationProcessResponse';
import { CheckOffContractRequest } from '../../../classes/CheckOffContractRequest';
import {
    ContractManagementRoutes,
    ContractManagementTabs,
} from '@forvalter/routing/Routes';
import { Loader } from '@klp/design/Loader';
import { Tooltip, Typography, Block, Heading } from '@klp/design';
import {
    handleRequiredFormFields,
    useMapUserRolesToCandidateGroup,
} from '../../../utils';
import { useSignalR } from '../../../services/signalR';

import { useSelector } from 'react-redux';
import { FormContainerKontraktsforvaltning } from '@klp/formgenerator/index';
import { contractregistrationSelectors } from '@klp/contractregistration/contractToRegistrationSlice';
import { showToast } from '@klpkonsern/ui';

type AssessRelevanceStepProps = {
    handleCompleteStep: () => void;
    activity: IContractToRegistrationProcessActivity;
    prosessInstansId: string;
};

const useStyles = makeStyles(() => ({
    D365: {
        maxWidth: '490px',
    },
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
}));

export default function AssessRelevanceStep({
    handleCompleteStep,
    activity,
    prosessInstansId,
}: AssessRelevanceStepProps) {
    const { t, i18n } = useTranslation();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const classes = useStyles();
    const history = useHistory();
    const [registrationStatus, setRegisterationStatus] = useState<{
        [key in 'registered' | 'notRegistered']: boolean;
    }>({
        registered: false,
        notRegistered: false,
    });
    const [registrationFlag, setRegistrationFlag] = useState<boolean>();
    const { kontraktId } = useParams<{ kontraktId: string }>();
    const { connected } = useSignalR();
    const { formFieldProcessing, formFieldError } = useSelector(
        contractregistrationSelectors,
    );

    const {
        data: contractToRegistration,
        isFetching: contractToRegistrationFetching,
    } = useGetContractToRegistrationQuery({
        kontraktId,
    });

    const {
        data: registrationProcessData,
        isFetching: registrationProcessFetching,
    } = useGetContractRegistrationProcessQuery({
        kontraktDokumentId: kontraktId,
        startNewProcess: false,
        signalRConnected: connected,
        culture: i18n.language,
    });
    const [
        registerContract,
        { isLoading: registerContractLoading, isError: registerContractError },
    ] = useRegisterContractMutation();

    const { isUserCandidateForActivity } =
        useMapUserRolesToCandidateGroup(activity);

    const formFieldIsRequired = handleRequiredFormFields(activity?.formFields);

    const baseIsDisabled =
        registrationFlag === undefined ||
        (activity.utfortDato !== null &&
            !contractToRegistration?.skalRegistreres) ||
        registrationProcessData?.registreringStatus ===
            RegistrationStatuses.FULLFORT ||
        !isUserCandidateForActivity;

    const formFieldIsDisabled = baseIsDisabled || activity.utfortDato !== null;

    const submitIsDisabled =
        baseIsDisabled ||
        (registrationFlag && (formFieldError || formFieldIsRequired)) ||
        (activity.utfortDato !== null && registrationFlag);

    const isLoading =
        contractToRegistrationFetching ||
        registrationProcessFetching ||
        registerContractLoading ||
        formFieldProcessing;

    useEffect(() => {
        if (contractToRegistration?.skalRegistreres) {
            setRegisterationStatus({
                registered: true,
                notRegistered: false,
            });
            setRegistrationFlag(true);
            return;
        }

        if (!contractToRegistration?.skalRegistreres) {
            setRegisterationStatus({
                registered: false,
                notRegistered: true,
            });
            setRegistrationFlag(false);
            return;
        }
    }, [contractToRegistration]);

    const handleComplete = async () => {
        if (handleCompleteStep) {
            handleCompleteStep();
        }

        if (!activity.id) {
            return;
        }

        await registerContract(
            new CheckOffContractRequest({
                dokumentId: contractToRegistration?.dokumentId,
                kontraktlinjeIds:
                    contractToRegistration?.avsjekkKontraktlinjeIds,
                kommentar: '',
                skalRegistreres: registrationFlag,
            }),
        );

        if (!registrationFlag) {
            history.push(
                ContractManagementRoutes.tab(
                    ContractManagementTabs.registrering,
                ),
            );
        }
    };

    if (registerContractError) {
        showToast({
            body: t('contractRegistration.error'),
            type: 'error',
        });
    }

    return (
        <>
            <Grid
                container={true}
                direction="column"
                justifyContent="flex-start"
                className={classes.D365}
            >
                <Grid item={true} md={true} lg={true}>
                    <Block paddingTop="fat" paddingLeft="compact">
                        <Heading
                            klpHeadingLevel="THREE"
                            klpColor="dark"
                            translation={`checkOffContract.step.${activity?.taskDefinitionId}.secondaryTitle`}
                        />
                    </Block>
                </Grid>
                <Grid item={true}>
                    <Block paddingLeft="compact" paddingTop="tiny">
                        <Typography>
                            {t(
                                `checkOffContract.step.${activity?.taskDefinitionId}.description`,
                            )}
                        </Typography>
                        <Block paddingTop="compact">
                            <FormControl disabled={baseIsDisabled}>
                                <RadioGroup
                                    row={true}
                                    aria-labelledby="demo-row-radio-buttons-group-label"
                                    name="row-radio-buttons-group"
                                >
                                    <FormControlLabel
                                        value={registrationStatus.notRegistered}
                                        checked={
                                            registrationStatus.notRegistered
                                        }
                                        defaultChecked={
                                            registrationStatus.notRegistered
                                        }
                                        control={<Radio />}
                                        label={t(
                                            `checkOffContract.step.${activity?.taskDefinitionId}.notRelevantButton`,
                                        )}
                                        onClick={() => {
                                            if (baseIsDisabled) return;

                                            setRegistrationFlag(false);
                                            setRegisterationStatus({
                                                registered: false,
                                                notRegistered: true,
                                            });
                                        }}
                                    />
                                    <Block paddingLeft="compact">
                                        <FormControlLabel
                                            value={
                                                registrationStatus.registered
                                            }
                                            checked={
                                                registrationStatus.registered
                                            }
                                            defaultChecked={
                                                registrationStatus.registered
                                            }
                                            control={<Radio />}
                                            label={t(
                                                `checkOffContract.step.${activity?.taskDefinitionId}.relevantButton`,
                                            )}
                                            onClick={() => {
                                                if (baseIsDisabled) return;

                                                setRegistrationFlag(true);
                                                setRegisterationStatus({
                                                    registered: true,
                                                    notRegistered: false,
                                                });
                                            }}
                                        />
                                    </Block>
                                </RadioGroup>
                            </FormControl>
                        </Block>
                    </Block>
                </Grid>
            </Grid>

            {activity.formFields && registrationFlag && (
                <Block>
                    <FormContainerKontraktsforvaltning
                        activity={activity}
                        prosessInstansId={prosessInstansId}
                        isDisabled={formFieldIsDisabled}
                    />
                </Block>
            )}

            <Block paddingTop="compact" className={classes.buttonWrapper}>
                <Tooltip
                    translation="checkOffContract.tooltip"
                    hideTooltip={!submitIsDisabled}
                >
                    <Button
                        disabled={submitIsDisabled || isLoading}
                        ref={anchorRef}
                        variant="contained"
                        color="secondary"
                        onClick={handleComplete}
                    >
                        {t(
                            `checkOffContract.step.${activity?.taskDefinitionId}.completeButton`,
                        )}
                    </Button>
                </Tooltip>
            </Block>
            <Loader isLoading={isLoading} />
        </>
    );
}
